import dayjs from 'dayjs';
import {useCallback, useContext, useMemo} from 'react';

import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {MainFilterContext} from '../../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../../provider/main-filter/main-filter-type';
import {useReviewCityList} from '../../../../../../service/reivew-analysis/review-cities/review-cities-hook';
import {
    ReviewAnalysisCitiesSortKeyType,
    ReviewCityType,
} from '../../../../../../service/reivew-analysis/review-cities/review-cities-type';
import {ReviewsAndAnswersStatisticsUnitMeasurementEnum} from '../../../../../../service/reviews-and-answers-statistics/reviews-and-answers-statistics';
import {useTableSorter} from '../../../../../../util/antd/table/table-sorter-hook';
import {getRangeFilterTagFormat} from '../../../reviews-analysis-helper';
import {useReviewsAnalysisFilter} from '../../../reviews-analysis-hook';
import {ReviewsAnalysisFilterEnum, ReviewsCompareEnum} from '../../../reviews-analysis-type';
import {citiesDefaultPageSize} from '../cities-page-const';
import {getCitiesTableSorterQueryValue, mergeCompareData} from '../cities-page-helper';
import {CityTableDataType} from '../cities-page-type';
import {CitiesTable} from '../cities-table/cities-table';

export function CitiesTablePeriod(): JSX.Element | null {
    const {getLocalizedString, localeName} = useLocale();
    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    const {singleFilter, comparePeriodFilter, filter, compareMode} = useReviewsAnalysisFilter();

    const {sorterQueryValue, setSorterOrder, getSortOrder} = useTableSorter<ReviewAnalysisCitiesSortKeyType>({
        getSorterQueryValue: getCitiesTableSorterQueryValue,
    });

    const {result, isInProgress, processError, pagination} = useReviewCityList(
        singleFilter,
        sorterQueryValue,
        mainFilterKey,
        {
            initialPageSize: citiesDefaultPageSize,
        }
    );

    const range: [Date | null, Date | null] = filter[ReviewsAnalysisFilterEnum.Range];
    const period: string = useMemo(() => {
        const [mainPeriodStart, mainPeriodEnd] = range;

        const appliedRange: [Date, Date] =
            mainPeriodStart && mainPeriodEnd
                ? [mainPeriodStart, mainPeriodEnd]
                : [dayjs().subtract(29, 'day').toDate(), dayjs().toDate()];

        return getRangeFilterTagFormat(localeName, appliedRange);
    }, [localeName, range]);

    const compareRange: [Date | null, Date | null] = filter[ReviewsAnalysisFilterEnum.CompareRange];
    const comparePeriod: string = useMemo(
        () => getRangeFilterTagFormat(localeName, compareRange),
        [localeName, compareRange]
    );

    const {
        result: compareResult,
        isInProgress: isCompareInProgress,
        processError: compareProcessError,
    } = useReviewCityList(comparePeriodFilter, sorterQueryValue, mainFilterKey, {
        initialPageSize: citiesDefaultPageSize,
    });

    const getDataSource = useCallback((array: Array<ReviewCityType>, isForExport?: boolean) => {
        return array.map<CityTableDataType>(
            (dataItem: ReviewCityType, index): CityTableDataType => ({
                ...dataItem,
                key: `${dataItem.cityId} ${index} ${isForExport && 'export'}`,
            })
        );
    }, []);

    const dataSource: Array<CityTableDataType> = useMemo<Array<CityTableDataType>>((): Array<CityTableDataType> => {
        if (compareMode === ReviewsCompareEnum.PERIOD) {
            return mergeCompareData(
                result?.results || [],
                compareResult?.results || [],
                getLocalizedString('REVIEWS_ANALYSIS__DASHBOARD__COMPARE_PERIOD_NAME', {period: comparePeriod})
            );
        }

        return getDataSource(result?.results || []);
    }, [compareMode, getDataSource, result?.results, compareResult?.results, getLocalizedString, comparePeriod]);

    if (Boolean(processError) || Boolean(compareProcessError)) {
        return null;
    }

    return (
        <CitiesTable
            dataSource={dataSource}
            getSortOrder={getSortOrder}
            isInProgress={isInProgress || isCompareInProgress}
            mainPeriod={period}
            pagination={pagination}
            setSortOrder={setSorterOrder}
            timeUnit={singleFilter.timeFormat || ReviewsAndAnswersStatisticsUnitMeasurementEnum.Minutes}
        />
    );
}
